import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo } from 'react';
import { Box, Paper } from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import CustomDatePicker from "../../common_components/daterangepicker/CustomDatePicker";
import CommonLoader from "../../common_components/CommonLoader";
import MetricCard from "../../common_components/MetricCard";
import ChannelPerformance from "./ChannelPerformance";
import ClientGroupHeader from '../../common_components/ClientGroupHeader';
import { fetchEmailMarketingReport, selectEmailMarketingData, selectEmailMarketingLoading, selectEmailMarketingError, clearEmailMarketingData } from '../../redux/emailMarketingSlice';
// ✅ Import Custom Hooks
import { useClientSelection, useDateRange } from '../../hooks';
import { useAccountOverview } from '../../context/AccountOverviewContext';
import TableToolbar from '../../common_components/TableToolbar';
import { triggerRefresh } from '../../redux/refreshSlice';
import { toast } from '../../common_components/Toast/ToastUtils';
// ✅ Helper functions
import { formatStat } from "../../helper/statsFormatter";
const EMAIL_MARKETING_SESSION_KEY = 'g8a_email_marketing_dates';
const getTrend = (diff) => {
    if (diff === undefined || diff === null || Number.isNaN(diff))
        return "neutral";
    if (diff > 0)
        return "up";
    if (diff < 0)
        return "down";
    return "neutral";
};
const getTrendText = (diff) => {
    if (diff === undefined || diff === null || Number.isNaN(diff))
        return undefined;
    return `${Math.abs(diff).toFixed(0)}%`;
};
const EmailMarketingReport = () => {
    const dispatch = useDispatch();
    // Archive mode (Active/Archive toggle in the header) → load inactive (archived) clients.
    const { isArchiveMode } = useAccountOverview();
    // ✅ Custom Hooks
    const { selectedClientId, selectedGroupAccountId, selectedId, clientList, selectedClient, clientOptions, groupAccountOptions, currentSelectedGroupAccountLabel, utilityLoading, handleClientChange: baseClientChange, handleGroupChange: baseGroupChange } = useClientSelection('email_marketing', { filterKlaviyo: true, status: isArchiveMode ? 'inactive' : 'active' });
    const [savedEmailDates] = React.useState(() => {
        try {
            return JSON.parse(sessionStorage.getItem(EMAIL_MARKETING_SESSION_KEY) || '{}');
        }
        catch {
            return {};
        }
    });
    const { dateRange, compareDateRange, dateRangeKey, compareDateRangeKey, isCompareOn, handleDateChange: handleDateChangeBase } = useDateRange({
        defaultComparison: true,
        defaultCompareType: 'mom',
        initialDateRange: savedEmailDates.dateRange || null,
        initialCompareDateRange: savedEmailDates.compareDateRange || null,
    });
    const handleDateChange = (data) => {
        handleDateChangeBase(data);
        try {
            sessionStorage.setItem(EMAIL_MARKETING_SESSION_KEY, JSON.stringify({
                dateRange: data.mainRange ? { start: data.mainRange.start?.toString(), end: data.mainRange.end?.toString() } : null,
                compareDateRange: data.compareRange ? { start: data.compareRange.start?.toString(), end: data.compareRange.end?.toString() } : null,
            }));
        }
        catch (_) { }
    };
    // ✅ Report Selectors
    const reportData = useSelector(selectEmailMarketingData);
    const reportLoading = useSelector(selectEmailMarketingLoading);
    const reportError = useSelector(selectEmailMarketingError);
    // ✅ Fetch report data function
    const fetchReportData = async () => {
        if (!selectedId || !dateRange?.start || !dateRange?.end)
            return;
        const params = {
            clientId: selectedId,
            parentId: selectedClientId,
            childId: selectedGroupAccountId,
            startDate: dateRange.start.toString(),
            endDate: dateRange.end.toString(),
            comparison: isCompareOn,
        };
        if (isCompareOn && compareDateRange?.start && compareDateRange?.end) {
            params.compareStartDate = compareDateRange.start.toString();
            params.compareEndDate = compareDateRange.end.toString();
        }
        await dispatch(fetchEmailMarketingReport(params));
    };
    const [isRefreshDisabled, setIsRefreshDisabled] = React.useState(false);
    const [activeTabKey, setActiveTabKey] = React.useState("");
    // ✅ Refresh Handler
    const handleRefresh = async () => {
        if (!selectedId || !dateRange?.start || !dateRange?.end || isRefreshDisabled)
            return;
        const dateRanges = [{
                startDate: dateRange.start.toString(),
                endDate: dateRange.end.toString()
            }];
        if (compareDateRange?.start && compareDateRange?.end) {
            dateRanges.push({
                startDate: compareDateRange.start.toString(),
                endDate: compareDateRange.end.toString()
            });
        }
        setIsRefreshDisabled(true);
        toast.success("Refresh in progress. You’ll receive an email once the process is complete.");
        try {
            await dispatch(triggerRefresh({
                clientId: selectedId,
                dateRange: dateRanges,
                moduleKey: "email_marketing"
            }));
            // Re-enable the button once the API returns (either synchronously completed or background task created)
            setIsRefreshDisabled(false);
        }
        catch (error) {
            console.error("Refresh error:", error);
            setIsRefreshDisabled(false);
        }
    };
    // ✅ Custom handlers with data clear
    const handleClientChange = (val) => {
        dispatch(clearEmailMarketingData());
        baseClientChange(val);
    };
    const handleGroupChange = (val) => {
        dispatch(clearEmailMarketingData());
        baseGroupChange(val);
    };
    // ✅ Fetch data when dependencies change
    useEffect(() => {
        if (utilityLoading)
            return;
        if (!selectedId || !dateRangeKey)
            return;
        // Ensure the selected client is valid in the filtered list before fetching
        const isValidClient = clientList.some((c) => c._id === selectedClientId || c.value === selectedClientId);
        if (!isValidClient)
            return;
        const params = {
            clientId: selectedId,
            parentId: selectedClientId,
            childId: selectedGroupAccountId,
            startDate: dateRange.start.toString(),
            endDate: dateRange.end.toString(),
            comparison: isCompareOn,
        };
        if (isCompareOn && compareDateRange?.start && compareDateRange?.end) {
            params.compareStartDate = compareDateRange.start.toString();
            params.compareEndDate = compareDateRange.end.toString();
        }
        dispatch(fetchEmailMarketingReport(params));
    }, [
        dispatch,
        selectedId,
        selectedClientId,
        selectedGroupAccountId,
        dateRangeKey,
        compareDateRangeKey,
        utilityLoading,
        clientList
    ]);
    // ✅ Stats memoized
    const statsObj = useMemo(() => {
        return reportData?.stats?.reduce((acc, curr) => ({ ...acc, ...curr }), {}) || {};
    }, [reportData?.stats]);
    // ✅ Loading State
    if (utilityLoading) {
        return (_jsx(Box, { className: "reusable-table-loading", sx: { p: 4, textAlign: "center" }, children: _jsx(CommonLoader, { className: "table-loader" }) }));
    }
    // ✅ No Klaviyo Clients
    if (!utilityLoading && clientList.length === 0) {
        return (_jsx(PageContainer, { children: _jsxs(Paper, { elevation: 0, sx: {
                    p: 4,
                    border: '1px solid #F59E0B',
                    borderRadius: '8px',
                    bgcolor: '#FFFBEB',
                    textAlign: 'center'
                }, children: [_jsx(Box, { sx: { color: '#B45309', fontSize: '16px', fontWeight: 500 }, children: "No clients with Klaviyo connection found." }), _jsx(Box, { sx: { color: '#92400E', fontSize: '14px', mt: 1 }, children: "Please connect Klaviyo to at least one client to view this report." })] }) }));
    }
    const totalRevenue = statsObj?.totalRevenue?.curr || 0;
    const emailContribution = totalRevenue
        ? Math.round((statsObj?.emailRevenue?.curr / totalRevenue) * 100)
        : 0;
    const flowContribution = totalRevenue
        ? Math.round((statsObj?.flowRevenue?.curr / totalRevenue) * 100)
        : 0;
    const campaignContribution = totalRevenue
        ? Math.round((statsObj?.campaignRevenue?.curr / totalRevenue) * 100)
        : 0;
    const smsContribution = totalRevenue
        ? Math.round((statsObj?.smsCampaignRevenue?.curr / totalRevenue) * 100)
        : 0;
    return (_jsx(PageContainer, { children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '8px' }, children: [_jsx(ClientGroupHeader, { selectedClientId: selectedClientId, clientOptions: clientOptions, handleClientChange: handleClientChange, selectedGroupAccountId: selectedGroupAccountId, groupAccountOptions: groupAccountOptions, handleGroupChange: handleGroupChange, currentSelectedGroupAccountLabel: currentSelectedGroupAccountLabel, isRefreshing: false, extraFilters: _jsx(Box, { width: 320, children: _jsx(TableToolbar, { showFullScreen: true, 
                            // showSearch
                            title: "" }) }) }), _jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: reportData?.lastUpdated, onRefresh: handleRefresh, disabled: isRefreshDisabled || reportLoading, disabledReason: isRefreshDisabled ? "Refresh is in process. You will get an email when it's refreshed" : undefined, moduleName: "email_marketing" }), rightSection: _jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: !reportError && (_jsx(CustomDatePicker, { value: dateRange, compareValue: compareDateRange, onApply: handleDateChange, comparison: true, placeholder: "Select Date Range", comparePlaceholder: "Compare Range", disabled: reportLoading })) }) }), reportLoading && (_jsx(Box, { className: "reusable-table-loading", sx: { p: 4, textAlign: "center" }, children: _jsx(CommonLoader, { className: "table-loader" }) })), reportError && !reportLoading && (_jsx(Paper, { elevation: 0, sx: {
                        p: 4,
                        border: '1px solid #F59E0B',
                        borderRadius: '8px',
                        bgcolor: '#FFFBEB',
                        textAlign: 'center'
                    }, children: _jsx(Box, { sx: { color: '#B45309', fontSize: '16px', fontWeight: 500 }, children: reportError }) })), !reportLoading && reportData && (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: { display: 'flex', gap: 1, flexWrap: 'wrap' }, children: [statsObj.totalRevenue && (_jsx(MetricCard, { title: "Total Revenue", tooltipText: statsObj.totalRevenue.shopify ? `This metric is derived from Shopify by using (${reportData?.formula})` : statsObj.totalRevenue.ga ? "This metric is derived from GA" : "No Shopify or GA network connected", icon: _jsx("img", { src: "/assets/revenue.svg", alt: "Total Revenue" }), value: statsObj.totalRevenue.noNetwork ? "Not Connected" : formatStat(statsObj.totalRevenue.curr, 'revenue'), comparisonValue: isCompareOn && !statsObj.totalRevenue.noNetwork ? formatStat(statsObj.totalRevenue?.prev, 'revenue') : undefined, percentageText: isCompareOn && !statsObj.totalRevenue.noNetwork ? getTrendText(statsObj.totalRevenue?.diff) : undefined, trend: statsObj.totalRevenue.noNetwork ? "neutral" : getTrend(statsObj.totalRevenue.diff) })), statsObj.emailRevenue && (_jsx(MetricCard, { title: "Email Revenue", tooltipText: "A sum of Flow Revenue + Campaigns Revenue.", icon: _jsx("img", { src: "/assets/mail.svg", alt: "Email Revenue" }), value: formatStat(statsObj.emailRevenue.curr, 'revenue'), comparisonValue: isCompareOn ? formatStat(statsObj.emailRevenue?.prev, 'revenue') : undefined, percentageText: isCompareOn ? getTrendText(statsObj.emailRevenue?.diff) : undefined, trend: getTrend(statsObj.emailRevenue.diff), contributionPercentage: `${emailContribution}%`, contributionPill: true })), statsObj.flowRevenue && (_jsx(MetricCard, { title: "Flows Revenue", tooltipText: "Flow revenue is extracted from Klaviyo using daily intervals, including both Email and SMS.", icon: _jsx("img", { src: "/assets/flowdata.svg", alt: "Flows Revenue" }), value: formatStat(statsObj.flowRevenue.curr, 'revenue'), comparisonValue: isCompareOn ? formatStat(statsObj.flowRevenue?.prev, 'revenue') : undefined, percentageText: isCompareOn ? getTrendText(statsObj.flowRevenue?.diff) : undefined, trend: getTrend(statsObj.flowRevenue.diff), contributionPercentage: `${flowContribution}%`, contributionPill: true })), statsObj.campaignRevenue && (_jsx(MetricCard, { title: "Campaigns Revenue", tooltipText: "Campaigns revenue is extracted from Klaviyo using daily intervals.", icon: _jsx("img", { src: "/assets/campaignr.svg", alt: "Campaigns Revenue" }), value: formatStat(statsObj.campaignRevenue.curr, 'revenue'), comparisonValue: isCompareOn ? formatStat(statsObj.campaignRevenue?.prev, 'revenue') : undefined, percentageText: isCompareOn ? getTrendText(statsObj.campaignRevenue?.diff) : undefined, trend: getTrend(statsObj.campaignRevenue.diff), contributionPercentage: `${campaignContribution}%`, contributionPill: true })), statsObj.smsCampaignRevenue && (_jsx(MetricCard, { title: "SMS/MMS Revenue", tooltipText: "SMS/MMS campaign revenue is extracted from Klaviyo using daily intervals.", icon: _jsx("img", { src: "/assets/smsem.svg", alt: "SMS Revenue" }), value: formatStat(statsObj.smsCampaignRevenue.curr, 'revenue'), comparisonValue: isCompareOn ? formatStat(statsObj.smsCampaignRevenue?.prev, 'revenue') : undefined, percentageText: isCompareOn ? getTrendText(statsObj.smsCampaignRevenue?.diff) : undefined, trend: getTrend(statsObj.smsCampaignRevenue.diff), contributionPercentage: `${smsContribution}%`, contributionPill: true }))] }), _jsx(ChannelPerformance, { clientId: selectedId, reportData: reportData, currentDateRange: dateRange, previousDateRange: compareDateRange, activeTabKey: activeTabKey, setActiveTabKey: setActiveTabKey })] }))] }) }));
};
export default EmailMarketingReport;
